import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MessageCircle, Shield, FileText, ClipboardList, UserCheck, Clock, Scale, HelpCircle, Building } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const HowInsuranceCompaniesHandleClaimsPage = () => {
  const faqData = [
    {
      question: "What does an insurance adjuster do after an accident?",
      answer: "An insurance adjuster evaluates claims on behalf of the insurance company. They review accident reports, medical records, treatment history, and insurance coverage to determine how the claim will be handled under the policy."
    },
    {
      question: "Why do insurance companies request recorded statements?",
      answer: "Recorded statements help adjusters gather information about how the accident happened and the extent of injuries. However, these statements become part of the claim file and may be used when evaluating the claim. It's often helpful to understand the purpose of these requests before responding."
    },
    {
      question: "Should I accept an early settlement offer from the insurance company?",
      answer: "Early settlement offers sometimes occur before the full extent of injuries and treatment are known. It's important to understand your options and make decisions carefully. Once you accept a settlement, you typically cannot seek additional compensation later."
    },
    {
      question: "How do insurance companies determine how much a claim is worth?",
      answer: "Insurance companies evaluate claims based on several factors including the severity of injuries, medical treatment and documentation, liability for the accident, and available insurance coverage. Understanding these factors helps injured clients better understand how settlement discussions develop."
    }
  ];

  const serviceData = {
    name: "Insurance Claims Information",
    description: "Educational information about how insurance companies handle personal injury claims and what injured clients should understand about the process.",
    category: "Personal Injury Law"
  };

  return (
    <div className="min-h-screen pt-20" data-testid="insurance-claims-page">
      <SEOHead
        title="How Insurance Companies Handle Injury Claims | Amy C. Brown Law"
        description="Understand how insurance companies evaluate personal injury claims. Learn about the adjuster's role, how claims are evaluated, and when legal guidance can help. Serving Everett and Snohomish County."
        keywords="insurance claims process, how insurance companies work, insurance adjuster, personal injury claim, settlement offer"
        canonicalUrl="https://amycbrownlaw.com/how-insurance-companies-handle-claims"
        faqData={faqData}
        serviceData={serviceData}
        localArea={{ city: "Everett", county: "Snohomish", state: "WA" }}
      />

      {/* Hero Section */}
      <section className="pt-36 pb-16 md:pt-40 md:pb-20 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-5xl">
          <p className="text-primary font-medium tracking-wide uppercase text-sm mb-4">Understanding the Process</p>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-secondary mb-8 leading-tight max-w-4xl">
            How Insurance Companies Handle Injury Claims
          </h1>
          <p className="text-xl md:text-2xl text-slate-600 leading-relaxed max-w-2xl">
            Knowledge is power. Understanding how insurers evaluate claims helps you make better decisions.
          </p>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <p className="text-lg md:text-xl text-slate-700 leading-relaxed mb-8">
            After an accident, many people quickly find themselves dealing with insurance adjusters, paperwork, and requests for information. Insurance companies handle injury claims every day—they have established processes for evaluating accidents, reviewing medical records, and determining how much to offer in settlement.
          </p>
          <p className="text-lg md:text-xl text-slate-700 leading-relaxed">
            Many people are unsure what the adjuster's role is, how settlement decisions are made, or what certain requests actually mean. This page explains how the insurance claims process typically works so you can feel more informed about your situation.
          </p>
        </div>
      </section>

      {/* The Role of the Insurance Adjuster */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <div className="flex items-center gap-3 mb-6">
            <UserCheck className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              The Role of the Insurance Adjuster
            </h2>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-8">
            The insurance adjuster's job is to evaluate claims on behalf of the insurance company. They are typically the person who contacts you after an accident and manages your claim throughout the process.
          </p>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            Adjusters review several types of information when evaluating a claim:
          </p>
          
          <div className="grid md:grid-cols-2 gap-5 mb-8">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <FileText className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-secondary mb-2">Accident Reports</h3>
              <p className="text-slate-600 text-sm">Police reports, incident reports, and documentation of how the accident occurred</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <ClipboardList className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-secondary mb-2">Medical Records</h3>
              <p className="text-slate-600 text-sm">Documentation of injuries, diagnoses, and treatment from healthcare providers</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-secondary mb-2">Treatment History</h3>
              <p className="text-slate-600 text-sm">The timeline and extent of medical care received for accident-related injuries</p>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-100">
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-semibold text-secondary mb-2">Insurance Coverage</h3>
              <p className="text-slate-600 text-sm">Policy limits, coverage types, and how the policy applies to the specific claim</p>
            </div>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            The adjuster's role is to determine how the claim will be handled under the insurance policy. Understanding this can help you better navigate conversations with them.
          </p>
        </div>
      </section>

      {/* How Claims Are Evaluated */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <Scale className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              How Claims Are Evaluated
            </h2>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            Insurance companies typically evaluate injury claims by looking at several factors. Understanding these factors can help you better understand how settlement discussions develop.
          </p>
          
          <div className="space-y-4 mb-6">
            <div className="bg-slate-50 rounded-lg p-5 border-l-4 border-primary">
              <h3 className="font-semibold text-secondary mb-2">Severity of Injuries</h3>
              <p className="text-slate-600">The nature and extent of physical injuries, including whether they are temporary or permanent</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-5 border-l-4 border-primary">
              <h3 className="font-semibold text-secondary mb-2">Medical Treatment and Documentation</h3>
              <p className="text-slate-600">The type of medical care received, the duration of treatment, and how well the injuries are documented</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-5 border-l-4 border-primary">
              <h3 className="font-semibold text-secondary mb-2">Liability for the Accident</h3>
              <p className="text-slate-600">Who was at fault for the accident and whether fault is clear or disputed</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-5 border-l-4 border-primary">
              <h3 className="font-semibold text-secondary mb-2">Available Insurance Coverage</h3>
              <p className="text-slate-600">The policy limits and types of coverage available to pay the claim</p>
            </div>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            If you'd like to learn more about how case values are determined, you can read our guide on <Link to="/how-much-is-my-case-worth" className="text-primary hover:underline">how much your case might be worth</Link>.
          </p>
        </div>
      </section>

      {/* Why Insurance Companies Request Statements and Records */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              Why Insurance Companies Request Statements and Records
            </h2>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            After an accident, insurance adjusters often request information from injured people. Common requests include:
          </p>
          
          <ul className="space-y-3 mb-6">
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700"><strong>Recorded statements</strong> about how the accident happened and your injuries</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700"><strong>Medical authorizations</strong> allowing the insurance company to obtain your medical records</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700"><strong>Documentation</strong> about the accident, injuries, and damages</span>
            </li>
          </ul>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            These requests are part of the claims evaluation process. However, it's often helpful to understand the purpose of these requests before responding.
          </p>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            If you're unsure about how to respond to requests from an insurance company, you may find our guide on <Link to="/should-i-talk-to-insurance" className="text-primary hover:underline">whether to talk to insurance</Link> helpful.
          </p>
        </div>
      </section>

      {/* Why Settlement Offers May Appear Early */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <Clock className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              Why Settlement Offers May Appear Early
            </h2>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            In some cases, insurance companies may make a settlement offer relatively quickly after an accident. While this might seem helpful, these early offers sometimes come before the full extent of injuries and treatment are known.
          </p>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            It's important to understand that once you accept a settlement, you typically cannot seek additional compensation later—even if your injuries turn out to be more serious than initially thought.
          </p>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            If you've received a settlement offer, making decisions carefully is important. Our guide on <Link to="/should-i-accept-settlement" className="text-primary hover:underline">whether to accept a settlement offer</Link> provides more information about what to consider.
          </p>
        </div>
      </section>

      {/* When Legal Guidance Can Help */}
      <section className="py-16 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <HelpCircle className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              When Legal Guidance Can Help
            </h2>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            Many injury claims are straightforward, but some become more complex. Situations where legal guidance can be particularly helpful include:
          </p>
          
          <ul className="space-y-3 mb-6">
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700">Injuries that require ongoing medical care or have long-term effects</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700">Cases where liability is disputed or unclear</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700">Questions about insurance coverage or multiple policies</span>
            </li>
            <li className="flex items-start gap-3">
              <div className="w-2 h-2 bg-primary rounded-full mt-2.5 flex-shrink-0"></div>
              <span className="text-slate-700">Settlement offers that seem low compared to your damages</span>
            </li>
          </ul>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            An attorney can help you understand your options and navigate the claims process. Whether you've been in a <Link to="/car-crashes" className="text-primary hover:underline">car accident</Link> or another type of incident, legal guidance can help ensure your interests are protected.
          </p>
        </div>
      </section>

      {/* Closing Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <div className="flex items-center gap-3 mb-6">
            <Building className="w-8 h-8 text-primary" />
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary">
              Making Informed Decisions About Your Claim
            </h2>
          </div>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            Dealing with insurance companies after an accident can feel overwhelming, especially while you're also trying to recover from injuries. The process involves unfamiliar terminology, multiple requests for information, and decisions that can significantly affect your future.
          </p>
          
          <p className="text-lg text-slate-700 leading-relaxed">
            My goal is to help clients understand the process and make informed decisions about their claims. If you have questions about how insurance companies are handling your claim, or if you'd like guidance about your options, you're welcome to <Link to="/contact" className="text-primary hover:underline">contact the office</Link> to schedule a conversation.
          </p>
        </div>
      </section>

      {/* Soft CTA */}
      <section className="py-16 bg-secondary">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Questions About Your Insurance Claim?
          </h2>
          <p className="text-slate-200 mb-8 text-lg">
            If you have questions about your situation or would like to discuss your case, you're welcome to contact the office to schedule a conversation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+14256751224"
              className="inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white px-8 py-4 rounded-full font-medium transition-all"
              data-testid="insurance-claims-call-btn"
            >
              <Phone className="w-5 h-5" />
              Call (425) 675-1224
            </a>
            <a
              href="sms:+14256751224"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/30 px-8 py-4 rounded-full font-medium transition-all"
              data-testid="insurance-claims-text-btn"
            >
              <MessageCircle className="w-5 h-5" />
              Text Me
            </a>
          </div>
        </div>
      </section>

      {/* Wikipedia Entity Links */}
      <section className="py-8 bg-slate-100">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <p className="text-sm text-slate-500 text-center">
            Related concepts:{' '}
            <a href="https://en.wikipedia.org/wiki/Insurance" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Insurance</a>
            {' · '}
            <a href="https://en.wikipedia.org/wiki/Claims_adjuster" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Claims Adjuster</a>
            {' · '}
            <a href="https://en.wikipedia.org/wiki/Personal_injury" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Personal Injury</a>
            {' · '}
            <a href="https://en.wikipedia.org/wiki/Settlement_(litigation)" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Settlement</a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default HowInsuranceCompaniesHandleClaimsPage;
